import { useEffect } from "react";
import {
  loadFromLocalStorage,
  saveToLocalStorage,
} from "../../../../common/helpers/localStorage";

const STORAGE_KEY = "ecommerce_recently_viewed";
const MAX_ITEMS = 12;

/**
 * Type guard to ensure data from storage is an array of numbers.
 */
const isNumberArray = (data: any): data is number[] => {
  return Array.isArray(data) && data.every((item) => typeof item === "number");
};

/**
 * Hook to track recently viewed products.
 * Adds the current productId to the top of the LocalStorage list.
 *
 * @param productId - The ID of the currently viewed product.
 */
export const useRecentlyViewedTracking = (
  productId: number | undefined | null,
) => {
  useEffect(() => {
    if (!productId) return;

    // 1. Load existing list
    const currentList = loadFromLocalStorage<number[]>(
      STORAGE_KEY,
      isNumberArray,
      [],
    );

    // 2. Remove the current ID if it already exists (to move it to the top)
    const filteredList = currentList.filter((id) => id !== productId);

    // 3. Add current ID to the front (LIFO - Most recent first)
    const newList = [productId, ...filteredList];

    // 4. Enforce limit
    if (newList.length > MAX_ITEMS) {
      newList.length = MAX_ITEMS;
    }

    // 5. Save back to storage
    saveToLocalStorage(STORAGE_KEY, newList);

    // Optional: Log for dev verification
    if (import.meta.env.DEV) {
      console.log("[RecentlyViewed] Updated list:", newList);
    }
  }, [productId]);
};
